import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBlog,
  FaPlus,
  FaTrash,
  FaEdit,
  FaEye,
  FaEyeSlash,
  FaSearch,
  FaTags,
} from "react-icons/fa";
import Pagination from "Components/Pagination";
import CommonModal from "Components/CommonModal";
import BlogCategoryManageModal from "Components/Blog/BlogCategoryManageModal";
import {
  catchError,
  changeState,
  commonModalSetting,
  enterFn,
} from "JS/common";
import {
  getBlogCategories,
  getBlogList,
  togglePublishBlog,
  deleteBlogPosts,
  updateBlogPost,
} from "JS/API";
import useQueryParams from "utils/useQueryParams";
import {
  BLOG_STATUS_OPTIONS,
  BLOG_CATEGORY_ALL_OPTION,
  BLOG_SECTION_OPTIONS,
  BLOG_SECTION_LABEL,
  BLOG_STATUS_LABEL,
  BLOG_STATUS_BADGE,
  BLOG_ERROR_MESSAGE,
  formatDate,
  normalizeErrorDetail,
} from "utils/blogConstants";
import { badgeStyles, detailPageStyles, listPageStyles } from "utils/designSystem";

const esc = (s) =>
  String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);

const EXPOSURE_FLAGS = {
  is_main: {
    label: "📌 전체 대표",
    badge: "전체 대표",
    unit: "대표글",
    help: "전체 탭 첫 칸, PICK 표시 (구역당 1개)",
    color: "#4f46e5",
    onBg: "#e0e7ff",
    scopeName: (post) => `${BLOG_SECTION_LABEL[post.category?.section] || "블로그"} 전체 탭`,
    sameScope: (a, b) => (a.category?.section || "blog") === (b.category?.section || "blog"),
  },
  is_featured: {
    label: "★ 카테고리 추천",
    badge: "추천",
    unit: "추천글",
    help: "카테고리 탭 첫 칸 (카테고리당 1개)",
    color: "#d97706",
    onBg: "#fef3c7",
    scopeName: (post) => post.category?.name || "이 카테고리",
    sameScope: (a, b) => a.category?.key === b.category?.key,
  },
};

const Blog = () => {
  const navigate = useNavigate();
  const { getQueryParamsObject, updateQueryParams } = useQueryParams();
  const initialQuery = getQueryParamsObject();

  const [pageInfo, setPageInfo] = useState({
    page: Number(initialQuery.page) || 1,
    totalPage: 1,
    limit: Number(initialQuery.limit) || 20,
  });
  const [status, setStatus] = useState(initialQuery.status || "all");
  const [section, setSection] = useState(initialQuery.section || "all");
  const [category, setCategory] = useState(initialQuery.category || "all");
  const [keyword, setKeyword] = useState(initialQuery.keyword || "");
  const [keywordInput, setKeywordInput] = useState(initialQuery.keyword || "");

  const [categories, setCategories] = useState([]);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [list, setList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);

  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({ mode: "", context: "", bool: false });
  // 모달 OK 시 실행할 콜백 (확인 플로우와 성공 후 리다이렉트 공용)
  const [pendingConfirm, setPendingConfirm] = useState(null);

  let prevent = false;

  // 확인 모달 오픈 + OK 콜백 등록
  const requestConfirm = (message, onConfirm) => {
    setPendingConfirm(() => onConfirm);
    commonModalSetting(setAlertBox, true, "confirm", message);
  };

  // 알럿 모달 (콜백 없이 단순 알림 / 혹은 OK 후 후속 동작)
  const requestAlert = (message, onConfirm) => {
    setPendingConfirm(onConfirm ? () => onConfirm : null);
    commonModalSetting(setAlertBox, true, "alert", message);
  };

  // API 호출 실패 통합 처리
  // 문자열/undefined(인증·서버): 공통 catchError 위임
  // error 객체(400/403권한/404/409/422): 페이지 레벨 토스트
  const handleApiFailure = async (errOrResult) => {
    if (typeof errOrResult === "string" || errOrResult === undefined) {
      await catchError(errOrResult, navigate, setAlertBox, setAlert);
      return;
    }
    const status = errOrResult?.response?.status;
    const detailText = normalizeErrorDetail(errOrResult?.response?.data?.detail);
    if (status === 403) {
      requestAlert(detailText || "권한이 없습니다.");
    } else if (status === 400 || status === 422) {
      requestAlert(detailText || "요청이 올바르지 않습니다.");
    } else if (status === 404) {
      requestAlert(detailText || "대상을 찾을 수 없습니다.");
    } else if (status === 409) {
      requestAlert(detailText || "중복된 요청입니다.");
    } else {
      requestAlert(BLOG_ERROR_MESSAGE.serverError);
    }
  };

  // 스타일
  const styles = {
    container: listPageStyles.container,
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px",
      flexWrap: "wrap",
      gap: "16px",
    },
    title: {
      fontSize: "28px",
      fontWeight: 700,
      color: "#0f172a",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    headerActions: { display: "flex", gap: "8px", flexWrap: "wrap" },
    createButton: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 18px",
      backgroundColor: "#2563eb",
      color: "#fff",
      border: "none",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: 600,
      cursor: "pointer",
    },
    deleteButton: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 16px",
      backgroundColor: "#dc2626",
      color: "#fff",
      border: "none",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: 500,
      cursor: "pointer",
    },
    categoryButton: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 16px",
      backgroundColor: "#fff",
      color: "#334155",
      border: "1px solid #cbd5e1",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: 500,
      cursor: "pointer",
    },
    filterBar: {
      backgroundColor: "#fff",
      padding: "16px",
      borderRadius: "12px",
      border: "1px solid #cbd5e1",
      boxShadow: "0 2px 4px -1px rgba(0,0,0,0.06)",
      display: "flex",
      gap: "12px",
      flexWrap: "wrap",
      alignItems: "center",
      marginBottom: "16px",
    },
    select: {
      padding: "8px 12px",
      border: "1px solid #cbd5e1",
      borderRadius: "6px",
      fontSize: "14px",
      backgroundColor: "#fff",
      color: "#1e293b",
      cursor: "pointer",
      minWidth: "140px",
    },
    searchWrap: { position: "relative", flex: "1 1 240px", minWidth: "220px" },
    searchInput: {
      width: "100%",
      padding: "8px 12px 8px 36px",
      border: "1px solid #cbd5e1",
      borderRadius: "6px",
      fontSize: "14px",
      color: "#1e293b",
    },
    searchIcon: {
      position: "absolute",
      left: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#9ca3af",
    },
    countInfo: { fontSize: "13px", color: "#6b7280", marginLeft: "auto" },
    tableContainer: {
      backgroundColor: "#fff",
      borderRadius: "12px",
      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
      border: "1px solid #cbd5e1",
      overflow: "hidden",
      marginBottom: "16px",
    },
    tableWrapper: detailPageStyles.tableWrapper,
    table: { width: "100%", borderCollapse: "collapse", minWidth: "900px" },
    th: {
      padding: "14px 12px",
      textAlign: "left",
      fontSize: "12px",
      fontWeight: 600,
      color: "#1e293b",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      backgroundColor: "#f1f5f9",
      borderBottom: "2px solid #cbd5e1",
      whiteSpace: "nowrap",
    },
    td: {
      padding: "12px",
      fontSize: "14px",
      color: "#1e293b",
      borderBottom: "1px solid #e2e8f0",
      verticalAlign: "middle",
    },
    titleCell: {
      fontWeight: 500,
      color: "#374151",
      cursor: "pointer",
      maxWidth: "420px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    badge: { ...badgeStyles.base, display: "inline-block" },
    iconButton: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "32px",
      height: "32px",
      borderRadius: "6px",
      border: "1px solid #cbd5e1",
      backgroundColor: "#fff",
      color: "#374151",
      cursor: "pointer",
      marginRight: "6px",
    },
    iconButtonDanger: { color: "#dc2626", borderColor: "#fecaca" },
    mainRow: { backgroundColor: "#eef2ff" },
    featuredRow: { backgroundColor: "#fffbeb" },
    exposureBadge: {
      ...badgeStyles.base,
      display: "inline-block",
      color: "#fff",
      marginRight: "6px",
    },
    exposureToggle: {
      padding: "4px 10px",
      marginRight: "4px",
      borderRadius: "999px",
      border: "1px solid #cbd5e1",
      backgroundColor: "#fff",
      color: "#94a3b8",
      fontSize: "12px",
      fontWeight: 600,
      whiteSpace: "nowrap",
      cursor: "pointer",
    },
    thHelp: {
      display: "block",
      fontSize: "10px",
      fontWeight: 400,
      color: "#64748b",
      textTransform: "none",
      letterSpacing: 0,
    },
    emptyState: {
      padding: "48px 24px",
      textAlign: "center",
      color: "#64748b",
      fontSize: "15px",
    },
  };

  const categoryOptions = useMemo(
    () => [
      BLOG_CATEGORY_ALL_OPTION,
      ...categories
        .filter((c) => section === "all" || (c.section || "blog") === section)
        .map((c) => ({ value: c.key, label: c.is_active === false ? `${c.name} (비노출)` : c.name })),
    ],
    [categories, section]
  );

  // 카테고리 로드 (1회)
  const loadCategories = async () => {
    try {
      const result = await getBlogCategories();
      if (typeof result === "object" && result?.data?.data) {
        setCategories(result.data.data);
      } else {
        await handleApiFailure(result);
      }
    } catch (err) {
      await handleApiFailure(err);
    }
  };

  // 목록 로드
  const loadList = async () => {
    if (prevent) return;
    prevent = true;
    setTimeout(() => {
      prevent = false;
    }, 200);

    try {
      const result = await getBlogList({
        page: pageInfo.page,
        limit: pageInfo.limit,
        status,
        section,
        category,
        keyword,
      });
      if (typeof result === "object" && result?.data) {
        setList(result.data.data || []);
        const meta = result.data.meta || {};
        setTotalCount(meta.total_count || 0);
        changeState(setPageInfo, "totalPage", meta.total_page || 1);
      } else {
        await handleApiFailure(result);
      }
    } catch (err) {
      await handleApiFailure(err);
    }
  };

  useEffect(() => {
    document.title = "마크픽 관리자 > 블로그 관리";
    loadCategories();
  }, []);

  useEffect(() => {
    loadList();
    updateQueryParams({
      page: pageInfo.page === 1 ? "" : pageInfo.page,
      limit: pageInfo.limit === 20 ? "" : pageInfo.limit,
      status: status === "all" ? "" : status,
      section: section === "all" ? "" : section,
      category: category === "all" ? "" : category,
      keyword: keyword || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo.page, pageInfo.limit, status, section, category, keyword]);

  // 필터 변경 시 1페이지로 리셋
  const onFilterChange = (setter, value) => {
    setter(value);
    if (pageInfo.page !== 1) changeState(setPageInfo, "page", 1);
  };

  // 구분이 바뀌면 이전 구분의 카테고리가 남지 않도록 초기화한다
  const onSectionChange = (value) => {
    setSection(value);
    setCategory("all");
    if (pageInfo.page !== 1) changeState(setPageInfo, "page", 1);
  };

  const onKeywordSubmit = () => {
    setKeyword(keywordInput.trim());
    if (pageInfo.page !== 1) changeState(setPageInfo, "page", 1);
  };

  // 단건/전체 선택
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const toggleSelectAll = () => {
    if (list.every((p) => selectedIds.includes(p.id))) {
      setSelectedIds([]);
    } else {
      setSelectedIds(list.map((p) => p.id));
    }
  };
  const isAllChecked = list.length > 0 && list.every((p) => selectedIds.includes(p.id));

  // 발행 토글
  const onTogglePublish = (post) => {
    const nextPublish = post.status !== "published";
    const label = nextPublish ? "발행" : "발행을 취소";
    requestConfirm(
      `정말 ${label}하시겠습니까?`,
      () => runTogglePublish(post.id, nextPublish)
    );
  };

  const runTogglePublish = async (id, nextPublish) => {
    try {
      const result = await togglePublishBlog(id, nextPublish);
      if (typeof result === "object" && result?.data?.data) {
        requestAlert(nextPublish ? "발행되었습니다." : "초안으로 변경되었습니다.");
        loadList();
      } else {
        await handleApiFailure(result);
      }
    } catch (err) {
      await handleApiFailure(err);
    }
  };

  // 노출 설정 토글
  const onToggleExposure = (post, flag) => {
    const { unit, scopeName, sameScope } = EXPOSURE_FLAGS[flag];
    if (post[flag]) {
      return requestConfirm(
        `'${esc(post.title)}'<br/>${unit} 지정을 해제하시겠습니까?`,
        () => runToggleExposure(post.id, flag, false)
      );
    }
    const scope = scopeName(post);
    const prev = list.find((p) => p[flag] && p.id !== post.id && sameScope(p, post));
    const draftNote =
      post.status !== "published" ? "<br/>초안이라 발행 전까지 서비스에 노출되지 않습니다." : "";
    const message = prev
      ? `${esc(scope)} ${unit}을 바꾸시겠습니까?<br/>'${esc(prev.title)}' → '${esc(post.title)}'${draftNote}`
      : `'${esc(post.title)}'을(를) ${esc(scope)} ${unit}로 지정하시겠습니까?<br/>기존 ${unit}이 있으면 해제됩니다.${draftNote}`;
    requestConfirm(message, () => runToggleExposure(post.id, flag, true));
  };

  const runToggleExposure = async (id, flag, next) => {
    const { unit } = EXPOSURE_FLAGS[flag];
    try {
      const result = await updateBlogPost(id, { [flag]: next });
      if (typeof result === "object" && result?.data?.data) {
        requestAlert(next ? `${unit}로 지정되었습니다.` : `${unit} 지정이 해제되었습니다.`);
        loadList();
      } else {
        await handleApiFailure(result);
      }
    } catch (err) {
      await handleApiFailure(err);
    }
  };

  // 삭제
  const onDeleteSingle = (id) => {
    requestConfirm(
      "해당 게시글을 삭제하시겠습니까?<br/>삭제된 글은 복구할 수 없습니다.",
      () => runDelete([id])
    );
  };

  const onDeleteMulti = () => {
    if (!selectedIds.length) {
      return requestAlert("삭제할 게시글을 선택해 주세요.");
    }
    const ids = [...selectedIds];
    requestConfirm(
      `선택한 ${ids.length}개 게시글을 삭제하시겠습니까?<br/>삭제된 글은 복구할 수 없습니다.`,
      () => runDelete(ids)
    );
  };

  const runDelete = async (ids) => {
    try {
      const result = await deleteBlogPosts(ids);
      if (typeof result === "object" && result?.data) {
        setSelectedIds([]);
        requestAlert("삭제되었습니다.");
        loadList();
      } else {
        await handleApiFailure(result);
      }
    } catch (err) {
      await handleApiFailure(err);
    }
  };

  const renderStatusBadge = (postStatus) => {
    const variant = BLOG_STATUS_BADGE[postStatus] || "neutral";
    return (
      <span style={{ ...styles.badge, ...badgeStyles[variant] }}>
        {BLOG_STATUS_LABEL[postStatus] || postStatus}
      </span>
    );
  };

  const renderRows = () => {
    return list.map((post) => {
      const checked = selectedIds.includes(post.id);
      return (
        <tr
          key={post.id}
          style={post.is_main ? styles.mainRow : post.is_featured ? styles.featuredRow : undefined}
        >
          <td style={styles.td} onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggleSelect(post.id)}
              style={{ width: "16px", height: "16px", cursor: "pointer" }}
            />
          </td>
          <td
            style={{ ...styles.td, ...styles.titleCell }}
            onClick={() => navigate(`/blog/${post.id}`)}
            title={post.title}
          >
            {Object.entries(EXPOSURE_FLAGS).map(
              ([flag, { badge, color }]) =>
                post[flag] && (
                  <span key={flag} style={{ ...styles.exposureBadge, backgroundColor: color }}>
                    {badge}
                  </span>
                )
            )}
            {post.title}
          </td>
          <td style={styles.td}>
            {post.category?.section
              ? BLOG_SECTION_LABEL[post.category.section] || post.category.section
              : "-"}
          </td>
          <td style={styles.td}>{post.category?.name || "-"}</td>
          <td style={styles.td}>{renderStatusBadge(post.status)}</td>
          <td style={styles.td}>
            {Object.entries(EXPOSURE_FLAGS).map(([flag, { label, help, color, onBg }]) => (
              <button
                key={flag}
                style={{
                  ...styles.exposureToggle,
                  ...(post[flag] ? { backgroundColor: onBg, borderColor: color, color } : {}),
                }}
                title={help}
                aria-pressed={!!post[flag]}
                onClick={() => onToggleExposure(post, flag)}
              >
                {label}
              </button>
            ))}
          </td>
          <td style={styles.td}>{formatDate(post.published_at)}</td>
          <td style={styles.td}>{formatDate(post.updated_at)}</td>
          <td style={styles.td}>
            <button
              style={styles.iconButton}
              title="편집"
              onClick={() => navigate(`/blog/${post.id}`)}
            >
              <FaEdit size={13} />
            </button>
            <button
              style={styles.iconButton}
              title={post.status === "published" ? "발행 취소" : "발행"}
              onClick={() => onTogglePublish(post)}
            >
              {post.status === "published" ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
            </button>
            <button
              style={{ ...styles.iconButton, ...styles.iconButtonDanger }}
              title="삭제"
              onClick={() => onDeleteSingle(post.id)}
            >
              <FaTrash size={12} />
            </button>
          </td>
        </tr>
      );
    });
  };

  const onModalOk = () => {
    if (alert === "logout") return navigate("/");
    const fn = pendingConfirm;
    setPendingConfirm(null);
    fn?.();
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          <FaBlog />
          BLOG
        </h1>
        <div style={styles.headerActions}>
          <button
            style={styles.deleteButton}
            onClick={onDeleteMulti}
            disabled={!selectedIds.length}
          >
            <FaTrash />
            <span>선택 삭제{selectedIds.length ? ` (${selectedIds.length})` : ""}</span>
          </button>
          <button style={styles.categoryButton} onClick={() => setIsCategoryModalOpen(true)}>
            <FaTags />
            <span>카테고리 관리</span>
          </button>
          <button style={styles.createButton} onClick={() => navigate("/blog/new")}>
            <FaPlus />
            <span>새 글 작성</span>
          </button>
        </div>
      </div>

      <div style={styles.filterBar}>
        <select
          value={status}
          onChange={(e) => onFilterChange(setStatus, e.target.value)}
          style={styles.select}
        >
          {BLOG_STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <select
          value={section}
          onChange={(e) => onSectionChange(e.target.value)}
          style={styles.select}
        >
          {BLOG_SECTION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <select
          value={category}
          onChange={(e) => onFilterChange(setCategory, e.target.value)}
          style={styles.select}
        >
          {categoryOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <div style={styles.searchWrap}>
          <FaSearch style={styles.searchIcon} size={14} />
          <input
            type="text"
            value={keywordInput}
            placeholder="제목/슬러그 검색"
            maxLength={100}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyDown={(e) => enterFn(e, onKeywordSubmit)}
            style={styles.searchInput}
          />
        </div>

        <select
          value={pageInfo.limit}
          onChange={(e) =>
            setPageInfo((prev) => ({ ...prev, page: 1, limit: Number(e.target.value) }))
          }
          style={{ ...styles.select, minWidth: "110px" }}
        >
          <option value={20}>20개씩</option>
          <option value={50}>50개씩</option>
          <option value={100}>100개씩</option>
        </select>

        <span style={styles.countInfo}>총 {totalCount}건</span>
      </div>

      <div style={styles.tableContainer}>
        {list.length ? (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: "40px" }}>
                    <input
                      type="checkbox"
                      checked={isAllChecked}
                      onChange={toggleSelectAll}
                      style={{ width: "16px", height: "16px", cursor: "pointer" }}
                    />
                  </th>
                  <th style={styles.th}>제목</th>
                  <th style={styles.th}>구분</th>
                  <th style={styles.th}>카테고리</th>
                  <th style={styles.th}>상태</th>
                  <th style={styles.th}>
                    노출 설정
                    <span style={styles.thHelp}>대표: 구역당 1개 · 추천: 카테고리당 1개</span>
                  </th>
                  <th style={styles.th}>발행일</th>
                  <th style={styles.th}>수정일</th>
                  <th style={styles.th}>액션</th>
                </tr>
              </thead>
              <tbody>{renderRows()}</tbody>
            </table>
          </div>
        ) : (
          <div style={styles.emptyState}>조건에 맞는 게시글이 없습니다.</div>
        )}
      </div>

      <Pagination pageInfo={pageInfo} setPageInfo={setPageInfo} />

      {isCategoryModalOpen && (
        <BlogCategoryManageModal
          onClose={() => setIsCategoryModalOpen(false)}
          onChanged={() => {
            loadCategories();
            loadList();
          }}
        />
      )}

      {alertBox.bool && (
        <CommonModal setModal={setAlertBox} modal={alertBox} okFn={onModalOk} />
      )}
    </div>
  );
};

export default Blog;
